import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ImageSourcePropType,
  ViewStyle,
} from 'react-native';
import { IconWithFallback } from './TokenIcon';
import { COLORS } from '../../theme/themeTokens';
import MarketChart from './MarketChart';
import type { V2RateHistoryPoint } from '../../types/v2';
import { calculatePeriodChange } from '../../utils/marketChart';
import { formatMarketPrice } from '../../utils/amountFormat';
import {
  CHART_HEIGHT, CHART_WIDTH, COLUMN_GAP, ICON_SIZE, LIST_ICON_COLUMN, NAME_MIN_WIDTH, PRICE_WIDTH,
} from './tokenRowLayout';

export interface TokenListCardData {
  /** Icon source (e.g. from getCurrencyIcon); ignored when iconNode is set */
  icon?: ImageSourcePropType;
  /** Bundled icon used when `icon` is a URL that fails to load. */
  iconFallback?: ImageSourcePropType;
  /** Short ticker/code, e.g. "BTC" — shown as the primary label */
  symbol: string;
  /** Full token name, e.g. "Bitcoin" — shown below the ticker */
  name: string;
  /** Token rate in USDT, e.g. 86473.1234 */
  rate: number;
  /** Decimals from the catalogue; without it the rate keeps 4 places. */
  rateDecimals?: number | null;
  /** Percentage change as a number, e.g. 2.34 or -1.12 */
  changePercent: number;
  history?: V2RateHistoryPoint[];
  /** Optional second line under the ticker, e.g. "Amazon Inc" for stocks. */
  subtitle?: string;
  /** Custom icon element; replaces `icon` when given (e.g. StockIcon). */
  iconNode?: React.ReactNode;
}

interface TokenListCardProps {
  data: TokenListCardData;
  /** Called when the card is pressed */
  onPress?: () => void;
  /** Optional outer container style (e.g. margins) */
  style?: ViewStyle;
}

const TokenListCard: React.FC<TokenListCardProps> = ({ data, onPress, style }) => {
  const historyChange = calculatePeriodChange(data.history ?? []);
  const trendIsNegative = (data.history?.length ?? 0) >= 2
    ? historyChange.absolute < 0
    : data.changePercent < 0;
  const formattedRate = formatMarketPrice(data.rate, data.rateDecimals) ?? 'n/a';

  const handlePress = () => {
    onPress?.();
  };

  return (
    <TouchableOpacity
      style={[styles.card, style]}
      onPress={handlePress}
      activeOpacity={0.75}
    >
      {data.iconNode ? (
        <View style={styles.iconSlot}>{data.iconNode}</View>
      ) : (
        <IconWithFallback source={data.icon} fallback={data.iconFallback} style={styles.icon} />
      )}

      <View style={styles.leftCol}>
        <Text style={data.subtitle ? styles.symbolWithSubtitle : styles.symbol} numberOfLines={1}>
          {data.symbol}
        </Text>
        {!!data.subtitle && (
          <Text style={styles.subtitle} numberOfLines={1}>
            {data.subtitle}
          </Text>
        )}
      </View>

      <View style={styles.gap} />
      <View style={styles.rightCol}>
        <Text style={styles.rate} numberOfLines={1}>
          {formattedRate}
        </Text>
      </View>
      {/* Spacers rather than margins: a margin cannot give way, and on a
          360dp phone something has to. See tokenRowLayout. */}
      <View style={styles.gap} />
      <View style={styles.chart}>
        <MarketChart
          points={data.history ?? []}
          height={CHART_HEIGHT}
          compact
          color={trendIsNegative ? '#E76A68' : '#35B99A'}
          emptyLabel="—"
        />
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 48,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderBottomWidth: 1,
    backgroundColor: COLORS.white,
    borderColor: COLORS.borderMuted,
  },
  icon: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    marginRight: LIST_ICON_COLUMN - ICON_SIZE,
  },
  iconSlot: {
    marginRight: LIST_ICON_COLUMN - ICON_SIZE,
  },
  // The name column takes the slack down to its floor and no further; a
  // long company name ellipsises into whatever it is left with.
  leftCol: {
    flex: 1,
    minWidth: NAME_MIN_WIDTH,
    justifyContent: 'center',
  },
  gap: {
    width: COLUMN_GAP,
    flexShrink: 1,
  },
  rightCol: {
    justifyContent: 'center',
    alignItems: 'flex-end',
    width: PRICE_WIDTH,
  },
  symbol: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  symbolWithSubtitle: {
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
    color: COLORS.textPrimary,
  },
  subtitle: {
    fontFamily: 'Inter',
    fontSize: 10,
    lineHeight: 13,
    color: COLORS.slate,
  },
  rate: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  chart: { width: CHART_WIDTH, height: CHART_HEIGHT, flexShrink: 1 },
});

export default TokenListCard;
